<template>
  <div
    :class="[
      'custom-tag',
      {
        disabled: disabled,
        removable: isRemovable,
        'is-clickable': clickEnabled || doubleClickEnabled,
      },
      variant,
      classes,
    ]"
    tabindex="0"
    :data-qa="`custom-tag-${value}`"
    @keyup.space="tagClicked($event)"
    @keydown.enter="tagClicked($event)"
    @keydown="checkDelete($event)"
    @click="tagClicked($event)"
    ref="customTagRef"
    v-b-tooltip="
      Utils.elementTreeOverflow(customTagRef) && !hideTooltip
        ? tooltipText
          ? tooltipText
          : value
        : ''
    "
  >
    <icon-warning-small v-if="['error', 'strikethrough'].includes(variant)" class="warning-icon" />
    <span class="tag-content ellipsis">
      {{ value }}
    </span>
    <icon-close-tag-small
      v-if="isRemovable"
      class="remove-button"
      @click="removeButtonClicked($event)"
    />
  </div>
</template>

<script setup lang="ts">
import IconWarningSmall from '../../assets/icons/IconWarningSmall.vue';
import IconCloseTagSmall from '../../assets/icons/IconCloseTagSmall.vue';
import { computed, ref } from 'vue';
import Utils from '../../helpers/Utils';

export type TagVariant = 'fill' | 'outline' | 'error' | 'strikethrough';

interface CustomTagProps {
  disabled?: boolean;
  value: string;
  variant?: TagVariant;
  removeOption?: boolean;
  classes?: string;
  tooltipText?: string;
  hideTooltip?: boolean;
  clickEnabled?: boolean;
  doubleClickEnabled?: boolean;
  isUpdateInProcess?: boolean;
}

const props = withDefaults(defineProps<CustomTagProps>(), {
  disabled: false,
  variant: 'fill',
  removeOption: false,
  classes: '',
  tooltipText: '',
  hideTooltip: false,
  clickEnabled: true,
});

interface CustomTagEmits {
  (e: 'remove'): void;

  (e: 'custom-tag-click'): void;

  (e: 'custom-tag-double-click'): void;
}

const emit = defineEmits<CustomTagEmits>();

const customTagRef = ref(null);

const isRemovable = computed(() => {
  return props.removeOption && !props.isUpdateInProcess;
});

const removeButtonClicked = (event: Event) => {
  emit('remove');
  if (!event.elementsClicked) {
    event.elementsClicked = [];
  }
  event.elementsClicked.push('CustomTagRemoveButton');
};

let clickTimer: ReturnType<typeof setTimeout> | null = null;
let clickCounter = 0;

const tagClicked = (event: Event) => {
  if (!event.elementsClicked?.includes('CustomTagRemoveButton')) {
    if (props.doubleClickEnabled) {
      clickCounter++;
      if (clickCounter === 1) {
        clickTimer = setTimeout(() => {
          clickCounter = 0;
          if (props.clickEnabled) {
            emit('custom-tag-click');
          }
        }, 500);
      }
      if (clickCounter === 2) {
        emit('custom-tag-double-click');
        if (!event.elementsClicked) {
          event.elementsClicked = [];
        }
        event.elementsClicked.push('CustomTagDoubleClick');
        clearTimeout(clickTimer);
        clickCounter = 0;
      }
    } else if (props.clickEnabled) {
      emit('custom-tag-click');
    }
  }
  clearFocus(event);
  if (!event.elementsClicked) {
    event.elementsClicked = [];
  }
  event.elementsClicked.push('CustomTag');
};

const clearFocus = (event: Event) => {
  if (event.pointerType === 'mouse') {
    (event.currentTarget as HTMLElement)?.blur();
  }
};

const checkDelete = (event: KeyboardEvent) => {
  if (['Backspace', 'Delete'].includes(event.code)) {
    emit('remove');
  }
};
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '../../assets/styles/colors' as colors;

.custom-tag {
  align-items: center;
  border: 1px solid transparent;
  border-radius: 10px;
  box-sizing: border-box;
  cursor: default;
  display: flex;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  height: 20px;
  line-height: 16px;
  max-width: 100%;
  outline: none;
  position: relative;
  width: fit-content;

  .remove-button {
    align-items: center;
    cursor: pointer;
    display: none;
    position: absolute;
    right: 4px;
  }

  &:hover:not(.disabled) .remove-button {
    display: flex;
  }

  &.is-clickable {
    cursor: pointer;
  }
}

.fill {
  background: map.get(colors.$colors, 'hawkes-blue');
  color: map.get(colors.$colors, 'fun-blue');
  padding: 2px 8px;

  &:hover {
    background: map.get(colors.$colors, 'selago');
  }

  &.removable:active {
    background: map.get(colors.$colors, 'selago');
    border-color: map.get(colors.$colors, 'fun-blue');
  }

  &.removable:focus:not(:active) {
    border-color: map.get(colors.$colors, 'malibu');
    box-shadow: 0 0 0 1px map.get(colors.$colors, 'malibu');
    color: map.get(colors.$colors, 'san-juan');
    outline: none;
  }

  &.disabled {
    background: map.get(colors.$colors, 'selago');
    color: map.get(colors.$colors, 'spindle');
    pointer-events: none;
  }
}

.outline {
  background: transparent;
  border-color: map.get(colors.$colors, 'dusty-gray');
  color: map.get(colors.$colors, 'dusty-gray');
  padding: 2px 8px;

  &:hover {
    background: map.get(colors.$colors, 'gallery');
    border-color: map.get(colors.$colors, 'silver');
  }

  &.removable:active {
    background: map.get(colors.$colors, 'gallery');
    border-color: map.get(colors.$colors, 'shuttle-gray');
    color: map.get(colors.$colors, 'bright-gray');
  }

  &.removable:focus:not(:active) {
    background: map.get(colors.$colors, 'white');
    box-shadow: 0 0 0 2px map.get(colors.$colors, 'malibu');
    outline: none;
  }

  &.disabled {
    background: map.get(colors.$colors, 'white');
    border-color: map.get(colors.$colors, 'silver');
    color: map.get(colors.$colors, 'silver');
    pointer-events: none;
  }
}

.error {
  background: map.get(colors.$colors, 'we-peep');
  color: map.get(colors.$colors, 'tall-poppy');
  padding: 2px 8px 2px 4px;

  .warning-icon {
    stroke: map.get(colors.$colors, 'tall-poppy');
  }

  &.removable:active {
    background: map.get(colors.$colors, 'linen');
    border-color: map.get(colors.$colors, 'tall-poppy');
  }

  &.removable:focus:not(:active) {
    border-color: map.get(colors.$colors, 'malibu');
    box-shadow: 0 0 0 1px map.get(colors.$colors, 'malibu');
    outline: none;
  }

  &.disabled {
    color: map.get(colors.$colors, 'petite-orchid');
    pointer-events: none;

    .warning-icon {
      stroke: map.get(colors.$colors, 'petite-orchid');
    }
  }
}

.strikethrough {
  background: map.get(colors.$colors, 'gallery');
  color: map.get(colors.$colors, 'dusty-gray');
  padding: 2px 8px 2px 4px;

  .warning-icon {
    stroke: map.get(colors.$colors, 'dusty-gray');
  }

  .tag-content {
    text-decoration: line-through;
  }

  &.removable:active {
    color: map.get(colors.$colors, 'shuttle-gray');

    .warning-icon {
      stroke: map.get(colors.$colors, 'shuttle-gray');
    }
  }

  &.removable:focus:not(:active) {
    border-color: map.get(colors.$colors, 'malibu');
    box-shadow: 0 0 0 1px map.get(colors.$colors, 'malibu');
    color: map.get(colors.$colors, 'shuttle-gray');
    outline: none;

    .warning-icon {
      stroke: map.get(colors.$colors, 'shuttle-gray');
    }
  }

  &.disabled {
    background: map.get(colors.$colors, 'alabaster');
    color: map.get(colors.$colors, 'silver');
    pointer-events: none;

    .warning-icon {
      stroke: map.get(colors.$colors, 'silver');
    }
  }
}

.tag-content {
  min-width: 40px;
  text-align: center;
}
</style>
